/**
 * Returns true while the user is still typing a dollar amount — allows a
 * trailing decimal point and up to 2 decimal digits, but nothing else.
 * Use this in onChange to silently reject invalid characters.
 *
 * Valid mid-entry examples: "", "1", "12.", "12.5", "12.50", ".9"
 */
export function isValidDollarInput(value: string): boolean {
  return value === '' || /^(\d+\.?\d{0,2}|\.\d{0,2})$/.test(value);
}

/**
 * Returns true if the string is a complete, well-formed dollar amount.
 * Use this to determine whether the final value is ready to save.
 *
 * Valid examples: "0", "1", "12.50", ".99"
 * Invalid examples: "12.", ".", ""
 */
export function isValidDollarAmount(value: string): boolean {
  return /^(\d+(\.\d{1,2})?|\.\d{1,2})$/.test(value);
}

/** Formats an in-progress dollar input to two decimal places without losing leading zeroes. */
export function formatDollarAmount(value: string): string {
  const raw = value.trim();
  if (!raw || raw === '.' || !isValidDollarInput(raw)) return value;

  const [whole = '', cents = ''] = raw.split('.', 2);
  return `${whole || '0'}.${cents.padEnd(2, '0')}`;
}

/**
 * Parses a validated dollar string to a float. Returns null for empty or
 * invalid input — pair with isValidDollarAmount before calling.
 */
export function parseDollarAmount(value: string): number | null {
  if (!isValidDollarAmount(value)) return null;
  return parseFloat(value);
}
